import { Injectable } from '@angular/core';
import { EntityService } from '@bo-client/shared/dynamic-search-serverside/entity-service';
import { forkJoin, Observable } from 'rxjs';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { CommissioningRequestDtoModel } from '@bo-client/activation-management/shared/models/commissioningRequestDto';
import { path } from '@bo-client/shared/constants/path';
import { Router } from '@angular/router';
import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatDialogSize } from '@bo-client/shared/constants/mat-dialog-size';
import { GenericTwoOptionsModalComponent } from '@bo-client/shared/modals/generic-two-options-modal/generic-two-options-modal.component';
import { uri } from '@bo-client/shared/constants/uri';
import { ToastrService } from 'ngx-toastr';
import { MetaService } from '@bo-client/core/api/meta-service';
import { AreaTypesModel } from '@bo-client/shared/models/areaTypes';
import { ValidAndExpiredPassModel } from '@bo-client/activation-management/shared/models/validPassWithUsersModel';
import { AdvancedFilterModel, ColumnState } from 'ag-grid-community';
import { CommissioningManagementService } from '@bo-client/activation-management/commissioning-management.service';
import { InstallerPassModel } from '@bo-client/shared/models/installerPassDto';
import { UserDto } from '@shared/models';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { CommissioningWithoutPassConstant } from '@bo-client/activation-management/commissioning/commissioning-home/commissioning-management.constans';

@Injectable()
export class CommissioningRequestListV2Service implements EntityService {
  baseUrl = '/commissioningrequest/filter/v2';
  areaTypes!: AreaTypesModel[];
  public sortedPassList: ValidAndExpiredPassModel = new ValidAndExpiredPassModel();
  public areaWithoutPassList: AreaTypesModel[] = [];
  private passes: InstallerPassModel[] = [];
  private currentUser: UserDto = null;

  constructor(
    private httpHapClient: HttpHapService,
    private metaService: MetaService,
    private router: Router,
    private authorizationService: AuthorizationService,
    private toastService: ToastrService,
    private matDialog: MatDialog,
    private commissioningManagementService: CommissioningManagementService,
    private dataShareService: DataShareService
  ) {
    this.dataShareService.userData$.pipe().subscribe((userData) => {
      this.currentUser = userData;
      this.loadPassData();
    });
  }

  loadData(
    tenant: string,
    environment: string,
    page: number,
    size: number,
    filterModel: AdvancedFilterModel,
    columnState: ColumnState[]
  ): Observable<{
    content: CommissioningRequestDtoModel[];
    totalElements: number;
  }> {
    const result = columnState
      .filter((item) => item.sort)
      .map((item) => ({ colId: item.colId, sort: item.sort }));

    const filterAndSortModel = {
      filterModel: filterModel,
      sortModel: result
    };

    return this.httpHapClient.postTyped<{
      content: CommissioningRequestDtoModel[];
      totalElements: number;
    }>(filterAndSortModel, `${this.baseUrl}?page=${page}&size=${size}`);
  }

  navigateToCreatePage(areaName: number) {
    // Todo: CommissioningManagementService ablösen
    this.commissioningManagementService.newCommissioning(areaName, this.areaTypes, this.passes);
  }

  navigateToEditPage(id: number | string) {
    this.router.navigate([path.cmPrefix + '/' + path.cmCommissioningManagement + path.edit + '/' + id], {
      replaceUrl: false
    });
  }

  deleteCommissioningRequest(commissioningRequest: CommissioningRequestDtoModel, onSuccess: () => void) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'commissioning-request-into-list-delete';
    dialogConfig.maxHeight = MatDialogSize.maxHeight;
    dialogConfig.width = MatDialogSize.width;
    dialogConfig.data = {
      title: 'Inbetriebnahme löschen?',
      message: 'Sind Sie sicher, dass Sie das Inbetriebnahme löschen möchten?',
      optionA: 'Abbrechen',
      optionB: 'Löschen bestätigen'
    };
    this.matDialog
      .open(GenericTwoOptionsModalComponent, dialogConfig)
      .afterClosed()
      .pipe()
      .subscribe(
        (result) => {
          if (result) {
            if (result == 'optionB') {
              this.deleteRegister(commissioningRequest, onSuccess);
            }
          }
        },
        () => {
          this.toastService.error('Beim Löschen ist ein Fehler aufgetreten');
        }
      );
  }

  isCommissioningRequestDeletable(state: string) {
    return this.hasDeletePermission && (state == 'Incomplete' || this.isAdmin);
  }

  private get isAdmin() {
    return this.authorizationService.hasRole('ROLE_EDITOR_BASE');
  }

  private get hasDeletePermission() {
    return this.authorizationService.hasPermission('COMMISSIONING_DELETE');
  }

  public get hasUpdatePermission() {
    return this.authorizationService.hasPermission('COMMISSIONING_UPDATE');
  }

  private deleteRegister(commissioningRequest: CommissioningRequestDtoModel, onSuccess: () => void) {
    this.httpHapClient
      .delete(uri.cmCommissioningRequest + '/' + commissioningRequest.id)
      .pipe()
      .subscribe(
        () => {
          this.toastService.success('Anfrage für Inbetriebnahme erfolgreich gelöscht.');
          onSuccess();
        },
        () => {
          this.toastService.error('Beim Löschen ist ein Fehler aufgetreten');
        }
      );
  }

  getRequestStatusTypes() {
    return this.metaService.getRequestStatus();
  }

  private loadPassData() {
    const currentTenantId = this.dataShareService.selectedTenant.id;
    const isTechnicianAdmin = this.authorizationService.canManageOtherUsersByAreas();
    const sources = [];
    sources.push(this.httpHapClient.get(uri.areaTypes));
    if (isTechnicianAdmin) {
      sources.push(this.httpHapClient.get(uri.tmGetAllPassesByOwnBusinessPartner));
    } else {
      sources.push(this.httpHapClient.get(uri.tmGetAllPassesByuser + '/' + this.currentUser.id));
    }
    forkJoin(...sources)
      .pipe()
      .subscribe((resArr) => {
        console.log('resArr', resArr);
        this.areaTypes = resArr[0] as AreaTypesModel[];
        this.passes = resArr[1] as InstallerPassModel[];
        if (isTechnicianAdmin) {
          this.passes = this.commissioningManagementService.filterAuthorisedPasses(
            this.currentUser,
            this.passes
          );
        }

        this.passes = this.commissioningManagementService.filterActiveAreas(this.passes);
        this.sortedPassList = this.commissioningManagementService.checkExpiredPasses(
          this.areaTypes,
          this.passes
        );
        this.areaWithoutPassList = this.areaTypes.filter((x) => {
          return (
            CommissioningWithoutPassConstant.findIndex(
              (c) =>
                c.area.toLowerCase() === x.name.toLowerCase() &&
                c.tenant.toLowerCase() === currentTenantId.toLowerCase()
            ) !== -1
          );
        });
      });
  }
}
